import AsyncStorage from '@react-native-async-storage/async-storage';
import { AreaType } from 'types/area';
import { Coordinate, RouteType } from 'types/map';
import { MarkerType } from 'types/marker';
import { NavigationData } from 'types/navigation';

const url = 'https://api.openrouteservice.org/v2/directions/foot-walking';

type OpenRouteServiceBody = {
  coordinates: number[][];
  units: 'mi';
  options?: {
    avoid_polygons?: {
      type: 'Polygon' | 'MultiPolygon';
      coordinates: number[][][][];
    };
  };
};

function calculateTime(duration: number) {
  const hours = Math.round(duration / 3600);
  return {
    hours,
    minutes: hours
      ? Math.round((duration / 3600) % 60)
      : Math.round(duration / 60),
  };
}

function setDetails(data: NavigationData, { name, color, id }): RouteType {
  const features = data.features;
  if (features && features[0]) {
    const coords = features[0].geometry.coordinates;
    const routes = coords.map((coord) => coord.reverse()) as [number, number][];
    const duration = features[0].properties.summary.duration;

    return {
      id,
      name,
      distance: Math.round(features[0].properties.summary.distance * 100) / 100,
      duration: calculateTime(duration),
      routes,
      color,
      segments: features[0].properties.segments.map((segment) => ({
        ...segment,
        distance: Math.round(segment.distance * 100) / 100,
        duration: calculateTime(segment.duration),
      })),
    };
  }

  return null;
}

export async function getPathData({
  routes,
  name,
  color,
  id,
  avoid,
}: {
  routes: Coordinate[];
  name?: string;
  color?: string;
  id?: string;
  avoid?: AreaType[];
}): Promise<RouteType> {
  const clone = [...routes];
  const modified = clone.map((coord) => [...coord].reverse());
  const body: OpenRouteServiceBody = { coordinates: modified, units: 'mi' };
  if (avoid?.length) {
    const modifiedAvoids = [...avoid]
      .map((a) => a.coordinates)
      .map((coord) => {
        const reversed = coord.map((c) => [...c].reverse());
        return [[...reversed, reversed[0]]];
      });

    body.options = {
      avoid_polygons: {
        coordinates: modifiedAvoids,
        type: 'MultiPolygon',
      },
    };
  }

  return fetch(`${url}/geojson`, {
    method: 'POST',
    headers: {
      Authorization: process.env.NAVIGATION_API_KEY,
      Accept:
        'application/json, application/geo+json, application/gpx+xml, img/png; charset=utf-8',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
    .then((res) => res.json())
    .then((data: NavigationData) => setDetails(data, { name, color, id }));
}

export async function saveCurrentDestinations(destinations: MarkerType[]) {
  if (destinations) {
    try {
      await AsyncStorage.setItem(
        'currentDestination',
        JSON.stringify(destinations),
      );
    } catch (e) {
      console.log('Error saving route', e);
    }
  } else {
    AsyncStorage.removeItem('currentDestination');
  }
}

export async function getCurrentDestinations() {
  const prev = await AsyncStorage.getItem('currentDestination');
  try {
    const json = JSON.parse(prev);
    if (json) {
      return json;
    }

    return null;
  } catch (err) {
    return null;
  }
}
